// =============================================================================
// PLATFORM FORMAT — the per-platform PASTE shaping (skill separator, bullet glyph,
// whether experience is included, section order), data-driven + sourced. Replaces
// the hardcoded 3-platform PLATFORM_FORMAT in render/presence_paste.ts, which falls
// back to LinkedIn's shaping for any other platform — a guess (Jason 2026-06-23:
// "fold it in now", same no-guessing rule as the criteria).
//
// One row per platform, validated like the criteria (Exa-backed, sourced). Until a
// platform's format is validated, the renderer keeps its safe LinkedIn default but
// the FORMAT IS FLAGGED for research — never silently passed off as correct.
// Cross-user / cross-platform (no userId). Additive only (rule #11).
// =============================================================================

import { index, integer, jsonb, pgTable, varchar } from 'drizzle-orm/pg-core';
import { id, lakeProvenance, timestamps } from './_shared';

export const platformFormat = pgTable(
  'platform_format',
  {
    id: id(),
    // One format per platform (same 11-bucket vocabulary).
    platform: varchar('platform', { length: 40 }).notNull().unique(),
    // The separator between skills, e.g. ', ' (LinkedIn) or '\n' (Seek). Literal.
    skillsSeparator: varchar('skills_separator', { length: 8 }),
    // The bullet glyph for experience bullets, e.g. '• '.
    bulletGlyph: varchar('bullet_glyph', { length: 8 }),
    // 1 = the platform shows a full experience section (LinkedIn); 0 = headline+about+skills.
    includeExperience: integer('include_experience'),
    // The section render order, e.g. ['headline','about','experience','skills','recommendedTitles'].
    sectionOrder: jsonb('section_order').$type<string[]>(),
    // 'validated'|'flagged'|'contested'|'rejected' — defaults flagged (researched, not yet validated).
    validationStatus: varchar('validation_status', { length: 16 }).notNull().default('flagged'),
    researchedBy: varchar('researched_by', { length: 64 }),
    confidence: integer('confidence'),
    ...lakeProvenance,
    ...timestamps,
  },
  (t) => ({
    platformIdx: index('platform_format_platform_idx').on(t.platform),
  }),
);
